<script lang="ts">
import AttachUploader from '@/app/common/components/AttachUploader.vue';
import { ATTACH_TYPE, FILE_COUNT_LIMIT, LANG_EN, OPINION, OPINION_OPTIONS } from '@/app/const';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { ComplaintDepartmentInfo } from '@/interface/Complaint.interface';
import { fileService } from '@/services/serviceProvider';
import { ref } from 'vue';

export default {
    components: {
        AttachUploader
    },
    props: {
        modelValue: {
            type: Object,
            default: {},
        },
        viewMode: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'displayAlertDialog'],
    setup() {
        const ascAnswerForm = ref();
        const fileSv = fileService;

        return { ascAnswerForm, fileSv };
    },
    data() {
        return {
            opinionOptions: OPINION_OPTIONS,
            fileCountLimit: FILE_COUNT_LIMIT,
            panel: [] as number[],
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
            },
        }
    },
    computed: {
        model: {
            get() {
                return this.modelValue as ComplaintDepartmentInfo;
            },
            set(newValue: any) {
                this.$emit('update:modelValue', newValue);
            }
        },
        displayAnswerUserSection() {
            return this.model.staffOpinionCode === OPINION.END_COMPLAINT
                || this.model.staffOpinionCode === OPINION.END_WITH_AGRMNT;
        },
        headers() {
            return [
                { title: this.$t('t-order'), sortable: false, key: 'index', align: 'center', width: '5%' },
                { title: this.$t('t-file-name'), key: 'fileName' },
                { title: '', key: 'actions', sortable: false, width: '2%' },
            ] as any[];
        },
    },
    methods: {
        async validateForm() {
            const res = this.ascAnswerForm ? await this.ascAnswerForm.validate() : true;
            return res.valid === undefined ? true : res.valid;
        },
        allAttachments(): any[] {
            let newUploads = this.model.fileUploads as any[] ?? [];
            newUploads.forEach(up => { up.type = ATTACH_TYPE.NEW });
            let alreadySaved = this.model.attachments as any[] ?? [];
            alreadySaved.forEach(saved => { saved.type = ATTACH_TYPE.SAVED });
            return newUploads.concat(alreadySaved);
        },
        checkDuplicateFileName() {
            let fileNameList = [] as string[];
            let allAttach = this.allAttachments();
            let allFilesUser = allAttach.map(o => {
                if (o.type === ATTACH_TYPE.NEW) {
                    return o.name;
                } else if (o.type === ATTACH_TYPE.SAVED) {
                    return o.fileName;
                }
            })
            allFilesUser.forEach(fileName => {
                if (fileNameList.includes(fileName)) {
                    this.$emit('displayAlertDialog', {
                        display: true,
                        messageTitle: this.$t('t-cannot-duplicate-file-name'),
                        messageText: this.$t('t-please-change-file-name'),
                    } as BaseAlert);
                    let index = this.model.fileUploads?.findIndex(o => o.name === fileName) as number;
                    if (index >= 0) {
                        this.model.fileUploads?.splice(index, 1);
                    }
                    return;
                } else {
                    fileNameList.push(fileName);
                }
            })
        },
        overSizeLimit() {
            this.$emit('displayAlertDialog', {
                display: true,
                messageTitle: this.$t('t-cannot-upload-over-size-limit'),
                messageText: this.$t('t-please-select-another-file'),
            } as BaseAlert);
        },
        errorEmptyFile() {
            this.$emit('displayAlertDialog', {
                display: true,
                messageTitle: this.$t("t-attachment-error-0byte"),
                messageText: this.$t('t-please-select-another-file'),
            } as BaseAlert);
        },
        deleteAttachment(attach: any) {
            let allAttachs = this.allAttachments();
            if (allAttachs && allAttachs.length > 0) {
                if (attach.type === ATTACH_TYPE.NEW) {
                    let newUploads = this.model.fileUploads;
                    this.model.fileUploads = newUploads?.filter(o => o.name !== attach.name);
                } else if (attach.type === ATTACH_TYPE.SAVED) {
                    let saved = this.model.attachments;
                    this.model.attachments = saved?.filter(o => o.fileName !== attach.fileName);
                }
            }
        },
    }
}
</script>
<template>
    <v-expansion-panels v-model="panel" class="form-card mb-6" multiple>
        <v-expansion-panel :value="1">
            <v-expansion-panel-title>
                <v-toolbar-title>{{ $t('t-complaint-asc-answer-back') }}</v-toolbar-title>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="px-6 pt-0">
                <v-form ref="ascAnswerForm" validate-on="submit lazy">
                    <v-row>
                        <v-col cols="12">
                            <div class="text-label">{{ $t('t-complaint-asc-remark') }}</div>
                            <v-textarea v-model="model.otherRemark" class="hide-float-label" variant="outlined"
                                hide-details="auto" density="comfortable" v-bind:placeholder="$t('t-please-input')"
                                :readonly="viewMode" no-resize>
                            </v-textarea>
                            <div class="textarea-export formatted-new-line mt-2" style="display: none;">
                                {{ model.otherRemark ?? '-' }}
                            </div>
                        </v-col>
                    </v-row>
                    <v-card class="inner-card no-shadow mt-6">
                        <v-card-text>
                            <div class="text-label mr-2 mt-1">{{ $t('t-complaint-airline-answer-attach') }}</div>
                            <v-row>
                                <v-col v-if="!viewMode" cols="12" class="upload-section" style="min-height: 130px;">
                                    <AttachUploader v-model="model.fileUploads" fileType="attach"
                                        :disabled="allAttachments().length >= fileCountLimit"
                                        @on-upload-success="checkDuplicateFileName" @on-over-size-limit="overSizeLimit"
                                        @on-error-empty-file="errorEmptyFile">
                                    </AttachUploader>
                                </v-col>
                                <v-col v-if="!viewMode" cols="12">
                                    <v-alert v-if="allAttachments() && allAttachments().length > 0" border="start"
                                        border-color="primary" :title="$t('t-upload-file-limit')" density="compact"
                                        variant="tonal" color="primary">
                                    </v-alert>
                                </v-col>
                                <v-col cols="12">
                                    <v-data-table :headers="headers" :items="allAttachments()" hide-default-footer
                                        :no-data-text="$t('t-no-data-available')" :items-per-page="-1">
                                        <template #[`item.index`]="{ index }">
                                            <div class="w-100 text-center">{{ index + 1 }}</div>
                                        </template>
                                        <template #[`item.fileName`]="{ item }">
                                            <span v-if="fileSv.isNewAttachment(item.type)">{{ item.name }}</span>
                                            <span v-if="fileSv.isSavedAttachment(item.type)">{{ item.fileName }}</span>
                                        </template>
                                        <template v-slot:item.actions="{ item, index }">
                                            <div class="actions d-flex align-center">
                                                <v-btn variant="text" color="primary"
                                                    @click="fileSv.viewFileInEditMode(item)">
                                                    <v-icon icon="mdi mdi-eye-outline" class="mr-1"></v-icon>
                                                    <span>{{ $t('t-view-doc') }}</span>
                                                </v-btn>
                                                <v-btn v-if="!viewMode" variant="text" color="error"
                                                    @click="deleteAttachment(item)">
                                                    <v-icon icon="mdi mdi-close" class="mr-1"></v-icon>
                                                    <span>{{ $t('t-delete') }}</span>
                                                </v-btn>
                                            </div>
                                        </template>
                                    </v-data-table>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-form>
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>
</template>